import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, MapPin, MessageCircle, Mail, Phone, Menu, X, User, LogOut, LayoutDashboard, ChevronDown, FlaskConical, GitBranch, Rocket, ShieldCheck, Sparkles, Building2, Stethoscope, Info, LayoutGrid, HeartPulse } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";
import { useAuth } from "@/contexts/AuthContext";
import { useTenantBrand } from "@/contexts/TenantBrandContext";
import ToothmatrixBanner from "@/components/ToothmatrixBanner";
import PdpplRenewalBanner from "@/components/PdpplRenewalBanner";
import { ImpersonationBanner } from "@/components/ImpersonationBanner";
import { VERSION } from "@/lib/version";
import { BrandImage, useDynamicFavicon } from "@/lib/brand";
import { TranslatorInit, LanguageSwitcher } from "@/lib/LanguageSwitcher";
import { ThemeToggle } from "@/lib/theme";

/* ------------------------------------------------------------------ */
/*  Mega-menu data — grouped, with icon + description per item        */
/* ------------------------------------------------------------------ */
function useMegaNav() {
    return [
        {
            id: "products",
            label: "Products",
            width: 700,
            // Two offerings: the physical lab (manufacturing) and the CRM platform,
            // with audience sub-groups (clinics / laboratories) inside the CRM column.
            columns: [
                {
                    eyebrow: "· Toothrocket Labs — manufacturing",
                    accent: "cyan",
                    mark: Rocket,
                    tagline: "The physical dental lab — we design, 3D-print and ship your restorations.",
                    items: [
                        { to: "/for-clinics", label: "Restorations & Catalog", icon: Stethoscope, desc: "Crowns · Veneers · Inlays · Splints · Guides · Sports · Bespoke — full clinical & product catalog." },
                        { to: "/workflow#send-case", label: "Send a Case", icon: ArrowUpRight, desc: "Upload STL / DICOM. Track from received → delivered. 10-yr warranty PDF on delivery." },
                        { to: "/solutions/dental-practice", label: "Free for Clinics", icon: Sparkles, desc: "No subscriptions or portal fees — you only pay for the work you order." },
                    ],
                },
                {
                    eyebrow: "· Toothmatrix — CRM software",
                    accent: "emerald",
                    mark: LayoutGrid,
                    tagline: "The platform that connects clinics and labs — scans, orders, live chat.",
                    groups: [
                        {
                            sub: "For clinics · free",
                            items: [
                                { to: "/solutions/dental-practice", label: "Toothmatrix Practice", icon: Stethoscope, desc: "One Scan Inbox for every intraoral scanner — 3Shape, DS Core, Medit, Shining 3D." },
                                { to: "/practice/register", label: "Register your practice", icon: ArrowUpRight, desc: "Portal link within one business day. Send scans, track orders, chat with the lab." },
                            ],
                        },
                        {
                            sub: "For laboratories",
                            items: [
                                { to: "/solutions/dental-labs", label: "Toothmatrix Lab", icon: Building2, desc: "Run your lab: cases, Kanban, invoicing, payroll — white-label, on your own subdomain." },
                                { to: "/pricing/labs", label: "Pricing for Labs", icon: Sparkles, desc: "Usage-based, in USD: from $99/mo for 100 cases. Yearly billing saves 15%." },
                                { to: "/toothmatrix", label: "Compare both products", icon: Info, desc: "One platform, two products — see how practices and labs work together." },
                            ],
                        },
                    ],
                },
            ],
        },
        {
            id: "about",
            label: "About Us",
            width: 460,
            items: [
                { to: "/technology", label: "Technology & Materials", icon: FlaskConical, desc: "Nano-ceramic hybrid resin, biocompatibility, accelerated wear." },
                { to: "/workflow", label: "Workflow", icon: GitBranch, desc: "From digital impression to chairside in 24 hours." },
                { to: "/rnd", label: "R&D", icon: Rocket, desc: "Pioneering nanotech printing · Asiga, Ketprint, BEGO, Sidra." },
                { to: "/about", label: "Our Story", icon: Info, desc: "Pune · Doha team, ISO-aligned process, and the 10-year warranty pact." },
            ],
        },
    ];
}

function Logo() {
    const { tenant } = useTenantBrand();
    // When on a tenant subdomain, replace the Toothrocket lockup with the
    // tenant's logo + company name. Master Toothmatrix/Toothrocket brand
    // stays as default on the platform root.
    if (tenant?.branding) {
        return (
            <div className="flex items-center gap-2.5" data-testid="brand-tenant">
                <Link to="/" className="flex items-center gap-2.5 group">
                    {tenant.branding.logo_url ? (
                        <img
                            src={tenant.branding.logo_url}
                            alt={tenant.branding.company_name || tenant.slug}
                            className="h-9 max-w-[140px] object-contain transition-transform group-hover:-translate-y-0.5"
                        />
                    ) : (
                        <div className="h-9 w-9 rounded-lg bg-slate-900 text-white flex items-center justify-center font-display text-base">
                            {(tenant.branding.company_name || tenant.slug || "?")[0].toUpperCase()}
                        </div>
                    )}
                    <span className="font-display font-medium text-[16px] tracking-tight text-slate-900 whitespace-nowrap leading-none">
                        {tenant.branding.company_name || tenant.slug}
                    </span>
                </Link>
                <span className="hidden sm:inline-block ml-1 pl-2.5 border-l border-slate-200 font-mono-tr text-[9px] tracking-[0.2em] uppercase text-slate-500 whitespace-nowrap">
                    Powered by<br />Toothmatrix
                </span>
            </div>
        );
    }
    return (
        <div className="flex items-center gap-2.5">
            <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group">
                <BrandImage slug="logo_primary" fallback="/brand/icon.png" alt="Toothrocket" className="h-11 w-11 object-contain transition-transform group-hover:-translate-y-0.5" />
                <span className="font-display font-medium text-[17px] tracking-tight text-slate-900 whitespace-nowrap leading-none">
                    toothrocket<span className="text-[10px] align-top ml-0.5 text-slate-400">™</span>
                </span>
            </Link>
            {/* Blue build pill — surfaces the deployment version right at the
                logo so anyone (operator, customer, auditor) can see what's
                shipping. Tooltip carries the release-count + ship date. */}
            <Link
                to="/compliance"
                data-testid="brand-version-pill"
                title={`Build v${VERSION.version} · ${VERSION.total_releases} production releases · last shipped ${VERSION.released_at}`}
                className="hidden sm:inline-flex items-center px-2 py-0.5 rounded-full bg-cyan-100 text-cyan-800 border border-cyan-200 font-mono-tr text-[10px] tracking-[0.14em] uppercase hover:bg-cyan-200 hover:text-cyan-900 transition-colors leading-none"
            >
                v{VERSION.version}
            </Link>
            <a
                href="https://mbkholding.qa"
                target="_blank"
                rel="noreferrer"
                data-testid="brand-mbk-holding"
                className="hidden sm:inline-block ml-1 pl-2.5 border-l border-slate-200 font-mono-tr text-[9px] tracking-[0.2em] uppercase text-slate-500 hover:text-cyan-600 transition-colors whitespace-nowrap"
            >
                Part of<br />MBK Holding ↗
            </a>
        </div>
    );
}

/* ------------------------------------------------------------------ */
/*  Dropdown trigger + mega-menu panel                                */
/* ------------------------------------------------------------------ */
function MegaItem({ it, setOpenId }) {
    const Icon = it.icon;
    return (
        <li>
            <NavLink
                to={it.to}
                data-testid={`nav-mega-link-${it.to.replace(/[/#]/g, "-").replace(/^-+/, "")}`}
                onClick={() => setOpenId(null)}
                className={({ isActive }) =>
                    `flex items-start gap-3 p-3 rounded-xl transition group/item ${
                        isActive ? "bg-cyan-50/70" : "hover:bg-slate-50"
                    }`
                }
            >
                <div className="shrink-0 mt-0.5 h-10 w-10 rounded-xl bg-gradient-to-br from-slate-900 to-slate-700 text-cyan-300 flex items-center justify-center group-hover/item:from-cyan-500 group-hover/item:to-teal-500 group-hover/item:text-white transition-all duration-300 shadow-sm">
                    <Icon size={17} />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                        <span className="font-display font-medium text-[14.5px] tracking-tight text-slate-900">{it.label}</span>
                        <ArrowUpRight size={12} className="text-slate-300 group-hover/item:text-cyan-600 group-hover/item:translate-x-0.5 group-hover/item:-translate-y-0.5 transition-all duration-300" />
                    </div>
                    <div className="mt-0.5 text-[12px] text-slate-500 leading-snug">{it.desc}</div>
                </div>
            </NavLink>
        </li>
    );
}

function MegaTrigger({ group, openId, setOpenId }) {
    const ref = useRef(null);
    const closeTimer = useRef(null);
    const isOpen = openId === group.id;

    const open = () => {
        if (closeTimer.current) clearTimeout(closeTimer.current);
        setOpenId(group.id);
    };
    const scheduleClose = () => {
        closeTimer.current = setTimeout(() => setOpenId((cur) => (cur === group.id ? null : cur)), 120);
    };

    const isMultiCol = Array.isArray(group.columns);
    const widthStyle = { width: `${group.width || 440}px` };

    return (
        <div
            ref={ref}
            onMouseEnter={open}
            onMouseLeave={scheduleClose}
            className="relative"
        >
            <button
                type="button"
                data-testid={`nav-trigger-${group.id}`}
                onClick={() => setOpenId(isOpen ? null : group.id)}
                aria-expanded={isOpen}
                aria-haspopup="menu"
                className={`group inline-flex items-center gap-1.5 px-3.5 py-2 text-[13.5px] font-medium tracking-tight rounded-full transition ${
                    isOpen ? "text-slate-900 bg-slate-100" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                }`}
            >
                {group.label}
                <ChevronDown
                    size={14}
                    className={`transition-transform duration-300 ${isOpen ? "rotate-180 text-cyan-600" : "text-slate-400 group-hover:text-slate-600"}`}
                />
            </button>

            {/* Mega-panel */}
            <div
                role="menu"
                data-testid={`nav-mega-${group.id}`}
                onMouseEnter={open}
                onMouseLeave={scheduleClose}
                style={widthStyle}
                className={`absolute left-1/2 -translate-x-1/2 top-full mt-3 rounded-2xl bg-white/95 backdrop-blur-2xl border border-slate-200/80 shadow-2xl shadow-slate-900/10 p-3 transition-all duration-200 origin-top ${
                    isOpen
                        ? "opacity-100 visible scale-100 translate-y-0"
                        : "opacity-0 invisible scale-[0.97] -translate-y-1 pointer-events-none"
                }`}
            >
                {/* arrow */}
                <div className="absolute -top-1.5 left-1/2 -translate-x-1/2 h-3 w-3 rotate-45 bg-white/95 border-l border-t border-slate-200/80" />
                {isMultiCol ? (
                    <div className="relative grid grid-cols-2 gap-3">
                        {group.columns.map((col) => {
                            const Mark = col.mark;
                            return (
                            <div key={col.eyebrow} className="rounded-xl bg-slate-50/60 p-2.5">
                                <div className="flex items-center gap-2 mb-1.5 px-2">
                                    {Mark && (
                                        <span className={`h-6 w-6 rounded-lg flex items-center justify-center text-white shrink-0 shadow-sm ${col.accent === "emerald" ? "bg-gradient-to-br from-emerald-500 to-teal-600" : "bg-gradient-to-br from-cyan-500 to-sky-600"}`}>
                                            <Mark size={13} />
                                        </span>
                                    )}
                                    <div className={`tr-overline ${col.accent === "emerald" ? "!text-emerald-600 dark:!text-emerald-400" : "!text-cyan-700"}`}>
                                        {col.eyebrow}
                                    </div>
                                </div>
                                {col.tagline && <p className="px-2 mb-2 text-[11.5px] leading-snug text-slate-500">{col.tagline}</p>}
                                {Array.isArray(col.groups) ? (
                                    col.groups.map((sg) => (
                                        <div key={sg.sub} className="mb-1">
                                            <div className="px-2 pt-1.5 pb-0.5 text-[10px] font-semibold uppercase tracking-[0.16em] text-slate-400">{sg.sub}</div>
                                            <ul className="space-y-0.5">
                                                {sg.items.map((it) => (
                                                    <MegaItem key={it.to} it={it} setOpenId={setOpenId} />
                                                ))}
                                            </ul>
                                        </div>
                                    ))
                                ) : (
                                    <ul className="space-y-0.5">
                                        {col.items.map((it) => (
                                            <MegaItem key={it.to} it={it} setOpenId={setOpenId} />
                                        ))}
                                    </ul>
                                )}
                            </div>
                            );
                        })}
                    </div>
                ) : (
                    <ul className="relative space-y-1">
                        {group.items.map((it) => (
                            <MegaItem key={it.to} it={it} setOpenId={setOpenId} />
                        ))}
                    </ul>
                )}
            </div>
        </div>
    );
}

export default function Layout({ children }) {
    const { t } = useI18n();
    const { user, logout } = useAuth();
    const { tenant: tenantBrand } = useTenantBrand();
    const isTenantSubdomain = !!tenantBrand;
    const MEGA = useMegaNav();
    const [scrolled, setScrolled] = useState(false);
    const [mobileOpen, setMobileOpen] = useState(false);
    const [userMenu, setUserMenu] = useState(false);
    const [openMega, setOpenMega] = useState(null);
    const loc = useLocation();
    useDynamicFavicon();

    const isPortalArea = loc.pathname.startsWith("/portal") || loc.pathname.startsWith("/admin") || loc.pathname.startsWith("/login");

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setMobileOpen(false);
        setUserMenu(false);
        setOpenMega(null);
        window.scrollTo({ top: 0, behavior: "instant" });
    }, [loc.pathname]);

    // Keep `<main>` top-padding equal to the live header height. The fixed header can
    // contain a Toothmatrix promo banner above the nav bar, so its height varies.
    const headerRef = useRef(null);
    const [headerHeight, setHeaderHeight] = useState(68);
    useEffect(() => {
        const el = headerRef.current;
        if (!el || typeof ResizeObserver === "undefined") return;
        const ro = new ResizeObserver(() => setHeaderHeight(el.offsetHeight));
        ro.observe(el);
        setHeaderHeight(el.offsetHeight);
        return () => ro.disconnect();
    }, []);

    return (
        <div className="min-h-screen flex flex-col bg-white">
            <TranslatorInit />
            <header
                ref={headerRef}
                data-testid="site-header"
                className={`fixed top-0 inset-x-0 z-50 transition-all duration-300 ${
                    scrolled || isPortalArea
                        ? "bg-white/85 backdrop-blur-xl border-b border-slate-200/70"
                        : "bg-white/40 backdrop-blur-md border-b border-transparent"
                }`}
            >
                {/* Cloaked-impersonation strip — renders nothing unless a
                    superadmin is currently "viewing as" a specific tenant.
                    Placed inside the fixed <header> so the ResizeObserver
                    below grows the `<main>` padding automatically. */}
                <ImpersonationBanner />
                {/* Marketing strip — only on the platform (not inside a tenant subdomain). */}
                {!isTenantSubdomain && <ToothmatrixBanner />}
                <div className="max-w-[1320px] mx-auto px-5 lg:px-6 h-[68px] flex items-center justify-between gap-3">
                    <Logo />
                    {!isPortalArea && (
                        <nav className="hidden lg:flex items-center gap-0.5" aria-label="Primary">
                            {MEGA.map((g) => (
                                <MegaTrigger key={g.id} group={g} openId={openMega} setOpenId={setOpenMega} />
                            ))}
                            <NavLink
                                to="/compliance"
                                data-testid="nav-compliance"
                                className={({ isActive }) =>
                                    `inline-flex items-center gap-1.5 px-3.5 py-2 text-[13.5px] font-medium tracking-tight rounded-full transition ${
                                        isActive ? "text-slate-900 bg-slate-100" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                                    }`
                                }
                            >
                                <ShieldCheck size={13} className="text-emerald-600" /> Compliance
                            </NavLink>
                            <NavLink
                                to="/case-studies"
                                data-testid="nav-case-study"
                                className={({ isActive }) =>
                                    `inline-flex items-center gap-1.5 px-3.5 py-2 text-[13.5px] font-medium tracking-tight rounded-full transition ${
                                        isActive ? "text-slate-900 bg-slate-100" : "text-slate-600 hover:text-slate-900 hover:bg-slate-50"
                                    }`
                                }
                            >
                                <HeartPulse size={13} className="text-rose-500" /> Case Studies
                            </NavLink>
                        </nav>
                    )}
                    <div className="flex items-center gap-1.5 shrink-0">
                        <ThemeToggle className="flex shrink-0" />
                        <LanguageSwitcher className="hidden sm:block" />

                        {user ? (
                            <div className="relative">
                                <button
                                    onClick={() => setUserMenu((v) => !v)}
                                    data-testid="user-menu-toggle"
                                    className="flex items-center gap-2 px-3 py-2 rounded-full bg-slate-100 hover:bg-slate-200 transition"
                                >
                                    <div className="h-6 w-6 rounded-full bg-[#0B1120] text-cyan-300 flex items-center justify-center text-[11px] font-bold">
                                        {(user.dentist_name || user.email)[0]?.toUpperCase()}
                                    </div>
                                    <span className="hidden md:inline text-[13px] font-medium text-slate-900 max-w-[120px] truncate">
                                        {user.clinic_name || user.email}
                                    </span>
                                </button>
                                {userMenu && (
                                    <div className="absolute end-0 mt-2 w-56 rounded-xl bg-white border border-slate-200 shadow-xl p-2 z-50">
                                        <Link to={user.role === "client" ? "/portal" : "/admin"} className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 text-[13.5px] text-slate-700">
                                            <LayoutDashboard size={14} />
                                            {user.role === "client" ? t("nav.portal") : t("nav.admin")}
                                        </Link>
                                        <button onClick={() => { logout(); setUserMenu(false); }} data-testid="header-logout" className="w-full flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-slate-50 text-[13.5px] text-slate-700">
                                            <LogOut size={14} /> {t("nav.logout")}
                                        </button>
                                    </div>
                                )}
                            </div>
                        ) : (
                            <Link
                                to="/login"
                                data-testid="header-login"
                                className="hidden md:inline-flex items-center gap-1.5 px-3 py-2 text-[13px] font-medium text-slate-700 hover:text-slate-900 transition whitespace-nowrap"
                            >
                                <User size={14} /> {t("nav.login")}
                            </Link>
                        )}

                        <Link
                            to="/workflow#send-case"
                            data-testid="header-send-case-cta"
                            style={{ display: 'none' }}
                            className="tr-btn !py-2.5 !px-4 text-[13px] whitespace-nowrap lg:!inline-flex"
                        >
                            {t("nav.send_case")}
                            <ArrowUpRight size={16} />
                        </Link>
                        <button
                            data-testid="mobile-menu-toggle"
                            onClick={() => setMobileOpen((v) => !v)}
                            className="lg:hidden p-2 rounded-md hover:bg-slate-100"
                            aria-label="Toggle menu"
                        >
                            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
                        </button>
                    </div>
                </div>
                {mobileOpen && (
                    <div className="lg:hidden border-t border-slate-200/70 bg-white flex flex-col" style={{ maxHeight: "calc(100vh - 68px)" }}>
                        <div className="px-5 py-3 flex flex-col overflow-y-auto flex-1">
                            {/* Auth + primary CTAs pinned at the TOP — always visible first */}
                            <div className="flex flex-col gap-2 pb-3 border-b border-slate-100 mb-2">
                                {!user ? (
                                    <Link
                                        to="/login"
                                        onClick={() => setMobileOpen(false)}
                                        data-testid="mobile-login-cta-top"
                                        className="w-full px-4 py-3 rounded-xl bg-slate-900 text-white text-center text-[14px] font-semibold inline-flex items-center justify-center gap-2"
                                    >
                                        {t("nav.login")} <ArrowUpRight size={15} />
                                    </Link>
                                ) : (
                                    <Link
                                        to={user.role === "client" ? "/portal" : "/admin"}
                                        onClick={() => setMobileOpen(false)}
                                        data-testid="mobile-portal-cta-top"
                                        className="w-full px-4 py-3 rounded-xl bg-slate-900 text-white text-center text-[14px] font-semibold inline-flex items-center justify-center gap-2"
                                    >
                                        {user.role === "client" ? "Open portal" : "Open admin"} <ArrowUpRight size={15} />
                                    </Link>
                                )}
                                <Link
                                    to="/workflow#send-case"
                                    onClick={() => setMobileOpen(false)}
                                    data-testid="mobile-send-case-cta"
                                    className="tr-btn !py-3 justify-center"
                                >
                                    {t("nav.send_case")}
                                    <ArrowUpRight size={16} />
                                </Link>
                            </div>
                            {MEGA.map((g) => {
                                const itemsToRender = Array.isArray(g.columns)
                                    ? g.columns.flatMap((c) => (Array.isArray(c.groups) ? c.groups.flatMap((sg) => sg.items) : c.items))
                                    : g.items;
                                return (
                                    <div key={g.id} className="py-3 border-b border-slate-100">
                                        <div className="tr-overline text-slate-500 mb-2">· {g.label}</div>
                                        <ul className="space-y-1">
                                            {itemsToRender.map((it) => {
                                                const Icon = it.icon;
                                                return (
                                                    <li key={it.to}>
                                                        <NavLink
                                                            to={it.to}
                                                            onClick={() => setMobileOpen(false)}
                                                            className={({ isActive }) =>
                                                                `flex items-center gap-3 p-2.5 rounded-lg ${
                                                                    isActive ? "bg-cyan-50 text-slate-900" : "text-slate-700 hover:bg-slate-50"
                                                                }`
                                                            }
                                                        >
                                                            <Icon size={16} className="text-cyan-600" />
                                                            <span className="text-[14.5px] font-medium">{it.label}</span>
                                                        </NavLink>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                );
                            })}
                            <NavLink to="/compliance" onClick={() => setMobileOpen(false)} className="py-3 text-[15px] font-medium border-b border-slate-100 text-slate-700 flex items-center gap-2">
                                <ShieldCheck size={14} className="text-emerald-600" /> Compliance
                            </NavLink>
                            <NavLink to="/case-studies" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-case-study" className="py-3 text-[15px] font-medium border-b border-slate-100 text-slate-700 flex items-center gap-2">
                                <HeartPulse size={14} className="text-rose-500" /> Case Studies
                            </NavLink>
                            <div className="flex items-center gap-3 pt-4 pb-2">
                                <ThemeToggle />
                                <LanguageSwitcher className="flex-1" />
                            </div>
                        </div>
                    </div>
                )}
            </header>

            <main className="flex-1" style={{ paddingTop: `${headerHeight}px` }}>
                <PdpplRenewalBanner />
                {children}
            </main>

            {!isPortalArea && <Footer t={t} />}
        </div>
    );
}

function Footer({ t }) {
    return (
        <footer
            data-testid="site-footer"
            className="relative tr-grain bg-[#0B1120] text-white overflow-hidden"
        >
            <div className="absolute -top-32 -left-20 w-[420px] h-[420px] tr-glow bg-cyan-500/30" />
            <div className="absolute -bottom-40 right-0 w-[520px] h-[520px] tr-glow bg-teal-500/25" />
            <div className="relative max-w-[1280px] mx-auto px-5 lg:px-8 pt-24 pb-10">
                <div className="grid lg:grid-cols-12 gap-10 lg:gap-6 items-end pb-16 border-b border-white/10">
                    <div className="lg:col-span-8">
                        <div className="tr-overline text-cyan-400 mb-4">· Ready when you are</div>
                        <h2 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl tracking-tight leading-[0.95]">
                            {t("footer.cta_a")}<br />
                            <span className="text-cyan-300/90 italic font-extralight">{t("footer.cta_b")}</span>
                        </h2>
                    </div>
                    <div className="lg:col-span-4 flex lg:justify-end">
                        <Link
                            to="/workflow#send-case"
                            data-testid="footer-send-case-cta"
                            className="inline-flex items-center gap-2 px-6 py-4 rounded-full bg-cyan-400 text-[#022c33] font-semibold hover:bg-cyan-300 transition"
                        >
                            {t("nav.send_case")}
                            <ArrowUpRight size={18} />
                        </Link>
                    </div>
                </div>

                <div className="grid grid-cols-2 lg:grid-cols-12 gap-10 pt-14">
                    <div className="col-span-2 lg:col-span-5">
                        <p className="text-white/65 text-sm leading-relaxed max-w-md">
                            A precision 3D-printing dental lab fabricating Nano Ceramic Hybrid Resin restorations with a <span className="text-white font-semibold">10-year clinical warranty</span>.
                        </p>
                        <div className="mt-7 grid sm:grid-cols-1 gap-6 text-[13px] text-white/75">
                            <div>
                                <div className="font-mono-tr text-[10.5px] tracking-[0.22em] uppercase text-cyan-400 mb-2.5">Qatar · Headquarters</div>
                                <div className="flex items-center gap-2 mb-1.5"><MapPin size={13} className="shrink-0 text-white/40" /> Al Shafi St · New Al Rayyan, Doha</div>
                                <a className="flex items-center gap-2 hover:text-cyan-300" href="tel:+97455060499" data-testid="footer-doha-phone"><Phone size={13} className="shrink-0 text-white/40" /> +974 5506 0499</a>
                                <div className="mt-1.5 text-[11.5px] text-white/50 font-mono-tr tracking-wide">CR · 42211</div>
                            </div>
                        </div>
                        <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-sm text-white/75">
                            <a className="flex items-center gap-2 hover:text-cyan-300" href="mailto:orders@toothrocket.com"><Mail size={14} className="text-white/40" /> orders@toothrocket.com</a>
                            <a className="flex items-center gap-2 hover:text-cyan-300" href="https://wa.me/97455060499" target="_blank" rel="noreferrer" data-testid="footer-whatsapp">
                                <MessageCircle size={14} className="text-white/40" /> WhatsApp
                            </a>
                        </div>
                    </div>

                    <div className="lg:col-span-2">
                        <div className="tr-overline text-cyan-400 mb-3">{t("footer.products")}</div>
                        <ul className="space-y-2 text-sm text-white/70">
                            <li><Link to="/for-clinics#restoratives" className="hover:text-white">Permanent Crowns</Link></li>
                            <li><Link to="/for-clinics#restoratives" className="hover:text-white">Same-Day Crowns</Link></li>
                            <li><Link to="/for-clinics#restoratives" className="hover:text-white">Veneers</Link></li>
                            <li><Link to="/for-clinics#restoratives" className="hover:text-white">Inlays / Onlays</Link></li>
                            <li><Link to="/for-clinics" className="hover:text-white">Full Catalog →</Link></li>
                        </ul>
                    </div>
                    <div className="lg:col-span-2">
                        <div className="tr-overline text-cyan-400 mb-3">{t("footer.lab")}</div>
                        <ul className="space-y-2 text-sm text-white/70">
                            <li><Link to="/technology" className="hover:text-white">Materials</Link></li>
                            <li><Link to="/workflow" className="hover:text-white">Workflow</Link></li>
                            <li><Link to="/rnd" className="hover:text-white">R&amp;D</Link></li>
                            <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                            <li>
                                <Link to="/toothmatrix" data-testid="footer-toothmatrix" className="inline-flex items-center gap-1.5 hover:text-white">
                                    Toothmatrix
                                    <span className="px-1.5 py-0.5 rounded-full bg-cyan-500/20 text-cyan-300 text-[9.5px] font-mono-tr tracking-wider uppercase">For labs</span>
                                </Link>
                            </li>
                            <li><Link to="/login" className="hover:text-white">{t("nav.portal")}</Link></li>
                            <li>
                                <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/docs/clinic-guide`}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="footer-clinic-guide"
                                    className="hover:text-cyan-300 inline-flex items-center gap-1.5"
                                >
                                    Clinic Guide ↗
                                </a>
                            </li>
                            <li>
                                <a
                                    href={`${process.env.REACT_APP_BACKEND_URL}/api/brochure.pdf`}
                                    target="_blank"
                                    rel="noreferrer"
                                    data-testid="footer-brochure"
                                    className="hover:text-cyan-300 inline-flex items-center gap-1.5"
                                >
                                    Product Brochure (PDF) ↗
                                </a>
                            </li>
                        </ul>
                    </div>
                    <div className="lg:col-span-3">
                        <div className="tr-overline text-cyan-400 mb-3">{t("footer.compliance")}</div>
                        <ul className="space-y-2 text-sm text-white/70">
                            <li>
                                <Link
                                    to="/trust"
                                    data-testid="footer-trust-center"
                                    className="inline-flex items-center gap-1.5 text-cyan-300 hover:text-cyan-200 font-medium transition"
                                >
                                    Trust Center
                                    <ArrowUpRight size={11} className="shrink-0 opacity-70" />
                                </Link>
                                <div className="text-[11.5px] text-white/40 mt-0.5 font-mono-tr tracking-wide">PDPPL · SOC 2 · ISO 27001 · GDPR · HIPAA</div>
                            </li>
                            <li>ISO 13485 process aligned</li>
                            <li>Patient privacy first</li>
                            <li>Digital Dentistry Integration</li>
                            <li>{t("common.warranty_label")}</li>
                        </ul>
                    </div>
                </div>

                <div className="mt-14 pt-6 border-t border-white/10 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3 text-xs text-white/40 font-mono-tr tracking-wider">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-1 sm:gap-3">
                        <div>© {new Date().getFullYear()} TOOTHROCKET LABS · ALL RIGHTS RESERVED</div>
                        {/* Build badge — proof of an evolving, audited codebase.
                            Links to /compliance so a curious reader lands on
                            the public Trust Center instead of guessing. */}
                        <Link
                            to="/compliance"
                            data-testid="footer-build-badge"
                            className="inline-flex items-center gap-1.5 hover:text-cyan-300 transition"
                            title={`Toothmatrix build v${VERSION.version} · ${VERSION.total_releases} production releases · last shipped ${VERSION.released_at}`}
                        >
                            <span className="h-1 w-1 rounded-full bg-cyan-400/60" />
                            v{VERSION.version} · {VERSION.total_releases} RELEASES
                        </Link>
                    </div>
                    <div className="flex items-center gap-2">
                        <span className="h-1.5 w-1.5 rounded-full bg-cyan-400 tr-pulse-dot" />
                        {t("footer.online")}
                    </div>
                </div>
            </div>
        </footer>
    );
}
